import { Menu } from '@base-ui/react/menu';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export interface FilterOption<T extends string> {
  value: T;
  label: string;
}

/* Options under a name, e.g. the organisations of one type. */
export interface FilterOptionGroup<T extends string> {
  label: string;
  options: readonly FilterOption<T>[];
}

export interface FilterSelectProps<T extends string> {
  /* Sits left of the button, e.g. "Weergave". */
  label: string;
  /* Reads the label to screen readers only, for a value that says what it is on its own. */
  hideLabel?: boolean;
  /* A flat list, groups, or loose options above groups. */
  options: readonly (FilterOption<T> | FilterOptionGroup<T>)[];
  value: T;
  onValueChange: (value: T) => void;
  /* What the button reads while no option matches the value. Defaults to the label. */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const itemClass = cn(
  'flex cursor-pointer items-center justify-between gap-3 rounded-sm px-2.75 py-2.25',
  'text-sm font-semibold text-ink outline-none transition-colors',
  'hover:bg-fill data-highlighted:bg-fill',
);

/*
 * The one filter control for list-page toolbars: a small label beside a grey
 * pill button, opening a menu that check-marks the active option.
 *
 * This is deliberately not the settings Select. A native select in a toolbar
 * reads as a setting you are changing rather than a view you are choosing, and
 * filter chips — the other thing this replaces — cannot show which facet they
 * belong to once there is more than one.
 */
export function FilterSelect<T extends string>({
  label,
  hideLabel = false,
  options,
  value,
  onValueChange,
  placeholder,
  disabled = false,
  className,
}: FilterSelectProps<T>) {
  const active = options
    .flatMap((option) => ('options' in option ? option.options : [option]))
    .find((option) => option.value === value);
  const shown = active?.label ?? placeholder ?? label;

  const item = (option: FilterOption<T>) => (
    <Menu.Item
      key={option.value}
      onClick={() => onValueChange(option.value)}
      className={itemClass}
    >
      {option.label}
      {option.value === value && (
        <Check className='size-3.25 shrink-0 stroke-3 text-ink' aria-hidden='true' />
      )}
    </Menu.Item>
  );

  return (
    <div
      data-slot='filter-select'
      className={cn('flex min-w-0 items-center gap-2', className)}
    >
      <span
        className={
          hideLabel ? 'sr-only' : 'shrink-0 text-xs font-semibold text-ink-muted'
        }
      >
        {label}
      </span>
      <Menu.Root disabled={disabled}>
        <Menu.Trigger
          className={cn(
            'flex min-w-0 cursor-pointer items-center gap-1.5 rounded-full bg-fill px-3.5 py-2',
            'text-sm font-semibold text-ink transition hover:bg-line',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
            'data-disabled:cursor-default data-disabled:text-ink-muted data-disabled:hover:bg-fill',
          )}
        >
          <span className='truncate'>{shown}</span>
          {!disabled && (
            <ChevronDown className='size-2.5 shrink-0 stroke-3' aria-hidden='true' />
          )}
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner side='bottom' align='start' sideOffset={6} className='z-50'>
            <Menu.Popup
              data-slot='filter-select-content'
              className='max-h-(--available-height) min-w-52 overflow-y-auto rounded-md border border-line bg-surface p-1.5 shadow-menu outline-none animate-pop'
            >
              {options.map((option) =>
                'options' in option ? (
                  <Menu.Group key={option.label}>
                    <Menu.GroupLabel className='px-2.75 pt-2.5 pb-1 text-xs font-bold tracking-widest text-ink-muted uppercase'>
                      {option.label}
                    </Menu.GroupLabel>
                    {option.options.map(item)}
                  </Menu.Group>
                ) : (
                  item(option)
                ),
              )}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </div>
  );
}
